import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DataSourceService {
  // Toggle between API and local files - set default to false (use API)
  private useLocalData = false;
  
  // Store section ID for file paths
  private currentSectionId: string;
  
  constructor(private http: HttpClient) { }
  
  // Public method to set data source toggle
  public setUseLocalData(value: boolean): void {
    this.useLocalData = value;
    console.log(`Data source switched to: ${this.useLocalData ? 'Local JSON files' : 'API endpoints'}`);
  }
  
  // Get current setting
  public getUseLocalData(): boolean {
    return this.useLocalData;
  }
  
  // Set section ID for local file paths
  public setSectionId(id: string): void {
    this.currentSectionId = id;
  }
  
  // Generic method to fetch data from either source
  public getData<T>(endpoint: string, baseUri: string, params?: any): Observable<T> {
    if (this.useLocalData) {
      return this.getLocalData<T>(endpoint, params);
    } else {
      return this.getServerData<T>(endpoint, baseUri, params);
    }
  }
  
  // Get data from server API
  private getServerData<T>(endpoint: string, baseUri: string, params?: any): Observable<T> {
    const url = `${baseUri}${endpoint}`;
    return this.http.get<T>(url).pipe(
      catchError(error => {
        console.error(`Error fetching data from server for ${endpoint}:`, error);
        return this.getLocalData<T>(endpoint, params); // Fallback to local data
      })
    );
  }
  
  // Get data from local JSON files
  private getLocalData<T>(endpoint: string, params?: any): Observable<T> {
    let filePath: string;
    
    // Map API endpoints to local file paths
    if (endpoint.includes('gradescale/get')) {
      filePath = `assets/${this.currentSectionId}/gradescale.json`;
    } else if (endpoint === 'citizenship') {
      filePath = `assets/${this.currentSectionId}/citizenship.json`;
    } else if (endpoint === 'workhabit') {
      filePath = `assets/${this.currentSectionId}/workhabit.json`;
    } else if (endpoint === 'comments') {
      filePath = `assets/${this.currentSectionId}/comments.json`;
    } else if (endpoint === 'Optel') {
      filePath = `assets/${this.currentSectionId}/Optel.json`;
    } else if (endpoint.includes('studentgrade/get')) {
      filePath = `assets/${this.currentSectionId}/studentgrade.json`;
    } else {
      console.warn(`No local file mapping for endpoint: ${endpoint}`);
      filePath = `assets/${this.currentSectionId}/${endpoint.split('?')[0]}.json`;
    }
    
    console.log(`Fetching local data from: ${filePath}`);
    return this.http.get<T>(filePath).pipe(
      catchError(error => {
        console.error(`Error fetching local data for ${filePath}:`, error);
        throw error;
      })
    );
  }
}